/**
 * Maps sidebar labels to backend module keys (user.permissions[].module).
 */
export const MENU_LABEL_TO_MODULE = {
  Dashboard: "dashboard",
  Appointments: "appointments",
  Billing: "billing",
  PatientRecords: "patientRecords",
  Schedule: "schedule",
  RevenueReports: "revenueReports",
  Settings: "settings",
};

const FRONTDESK_RESTRICTED_MODULES = new Set(["revenuereports", "settings"]);

function normalizeModuleKey(moduleKey) {
  return String(moduleKey || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isFrontDeskUser(user) {
  const role = String(user?.role || "").toLowerCase();
  return role === "staff" || role === "frontdesk" || role === "front_desk";
}

const MENU_ORDER = [
  "Dashboard",
  "Appointments",
  "Billing",
  "PatientRecords",
  "Schedule",
  "RevenueReports",
  "Settings",
];

export function getStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
  }
}

/** Main admin: full UI access */
export function isAdminUser(user) {
  return user && String(user.role || "").toLowerCase() === "admin";
}

/**
 * Legacy users: permissionsExplicit false and empty permissions → full module access.
 */
export function hasLegacyFullAccess(user) {
  if (!user || isAdminUser(user)) return true;
  if (user.permissionsExplicit === true) return false;
  const perms = user.permissions;
  return !Array.isArray(perms) || perms.length === 0;
}

export function permissionForModule(user, moduleKey) {
  if (!user || !moduleKey) return null;
  const list = Array.isArray(user.permissions) ? user.permissions : [];
  return list.find((p) => p && p.module === moduleKey) || null;
}

export function canViewModule(user, moduleKey) {
  if (!user || !moduleKey) return false;
  if (
    isFrontDeskUser(user) &&
    FRONTDESK_RESTRICTED_MODULES.has(normalizeModuleKey(moduleKey))
  ) {
    return false;
  }
  if (isAdminUser(user)) return true;
  if (hasLegacyFullAccess(user)) return true;
  const p = permissionForModule(user, moduleKey);
  return !!(p && p.view);
}

export function canEditModule(user, moduleKey) {
  if (!user || !moduleKey) return false;
  if (
    isFrontDeskUser(user) &&
    FRONTDESK_RESTRICTED_MODULES.has(normalizeModuleKey(moduleKey))
  ) {
    return false;
  }
  if (isAdminUser(user)) return true;
  if (hasLegacyFullAccess(user)) return true;
  const p = permissionForModule(user, moduleKey);
  return !!(p && p.edit);
}

export function canAccessMenuLabel(user, label) {
  if (!user) return true;
  const key = MENU_LABEL_TO_MODULE[label];
  if (!key) return false;
  return canViewModule(user, key);
}

/** Ordered list of sidebar labels the user may see */
export function visibleMenuLabels(user) {
  if (!user) return [...MENU_ORDER];
  return MENU_ORDER.filter((label) => {
    const key = MENU_LABEL_TO_MODULE[label];
    if (!key) return false;
    return canViewModule(user, key);
  });
}

/** URL path segment (no leading slash) for History API */
export const MENU_LABEL_TO_PATH = {
  Dashboard: "dashboard",
  Appointments: "appointments",
  Billing: "billing",
  PatientRecords: "patientrecords",
  Schedule: "schedule",
  RevenueReports: "revenuereports",
  Settings: "settings",
};

export function pathForMenuLabel(label) {
  return MENU_LABEL_TO_PATH[label] || "dashboard";
}

/** First allowed menu, or null if explicit permissions grant no modules */
export function firstAllowedMenuLabel(user) {
  if (!user) return null;
  const v = visibleMenuLabels(user);
  return v.length ? v[0] : null;
}
